'use client';

import React, { useEffect, useRef, useState } from 'react';

import { ChevronDown } from '@depop/web-thrift-ui/Icons/ChevronDown';
import { Close } from '@depop/web-thrift-ui/Icons/Close';
import { Body, Caption, Heading } from '@depop/web-thrift-ui/Typography';
import { VisuallyHidden } from '@depop/web-ui-kit/VisuallyHidden';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';

import {
  STICKY_CTA_APP_ICON,
  STICKY_CTA_BRANCH_CAMPAIGN,
  STICKY_CTA_BRANCH_CHANNEL,
  STICKY_CTA_BRANCH_FEATURE,
  STICKY_CTA_BRANCH_STAGE,
  STICKY_CTA_DOWNLOAD_REFERRER_SOURCE,
  STICKY_CTA_ID,
  STICKY_CTA_QR_REFERRER_SOURCE,
} from './constants';
import styles from './styles.module.css';

import {
  EBAY_STICKY_CTA_BRANCH_FEATURE,
  EBAY_STICKY_CTA_DOWNLOAD_REFERRER_SOURCE,
  EBAY_STICKY_CTA_ID,
  EBAY_STICKY_CTA_QR_REFERRER_SOURCE,
} from '@/app/[locale]/ebay/components/EbayStickyAppCta/constants';
import { BranchQRCode } from '@/components/BranchQRCode';
import { ButtonMinimal } from '@/components/ButtonMinimal';
import { ExtendedLink } from '@/components/ExtendedLink';
import { ActivityTrackerEventType } from '@/modules/activityTracker/constants';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker';
import { useCurrentRoute } from '@/modules/activityTracker/useCurrentRoute';
import { GET_APP_LINK } from '@/modules/branch/constants';
import { useBranchDeepLink } from '@/modules/externalTracking/useBranchDeepLink';

const PANEL_ID = 'sticky-app-cta-panel';

const BRANCH_LINK_PARAMS = {
  fallback: GET_APP_LINK,
  appPath: '/',
  stage: STICKY_CTA_BRANCH_STAGE,
  channel: STICKY_CTA_BRANCH_CHANNEL,
  campaign: STICKY_CTA_BRANCH_CAMPAIGN,
};

const STICKY_CTA_PARAMS = {
  default: {
    ctaId: STICKY_CTA_ID,
    feature: STICKY_CTA_BRANCH_FEATURE,
    qrReferrerSource: STICKY_CTA_QR_REFERRER_SOURCE,
    downloadReferrerSource: STICKY_CTA_DOWNLOAD_REFERRER_SOURCE,
  },
  ebay: {
    ctaId: EBAY_STICKY_CTA_ID,
    feature: EBAY_STICKY_CTA_BRANCH_FEATURE,
    qrReferrerSource: EBAY_STICKY_CTA_QR_REFERRER_SOURCE,
    downloadReferrerSource: EBAY_STICKY_CTA_DOWNLOAD_REFERRER_SOURCE,
  },
};

export function StickyAppCta({ ebay = false }: { ebay?: boolean }) {
  const t = useTranslations('home');
  const [isExpanded, setIsExpanded] = useState(false);
  const hasInteracted = useRef(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const [cardHeight, setCardHeight] = useState<number>();

  const { sendActivityTrackerEvent } = useActivityTracker();
  const page = useCurrentRoute();
  const urlPath = usePathname();

  const { ctaId, feature, qrReferrerSource, downloadReferrerSource } =
    STICKY_CTA_PARAMS[ebay ? 'ebay' : 'default'];

  const qrDeepLink = useBranchDeepLink({
    ...BRANCH_LINK_PARAMS,
    feature,
    referrerSource: qrReferrerSource,
  });
  const downloadDeepLink = useBranchDeepLink({
    ...BRANCH_LINK_PARAMS,
    feature,
    referrerSource: downloadReferrerSource,
  });

  useEffect(() => {
    if (!hasInteracted.current) {
      return;
    }
    (isExpanded ? panelRef.current : triggerRef.current)?.focus();
  }, [isExpanded]);

  useEffect(() => {
    const card = cardRef.current;
    if (!card || typeof ResizeObserver === 'undefined') {
      return;
    }
    const observer = new ResizeObserver(() => {
      if (card.scrollHeight > 0) {
        setCardHeight(card.scrollHeight);
      }
    });
    for (const child of card.children) {
      observer.observe(child);
    }
    return () => observer.disconnect();
  }, []);

  function sendInteractionEvent(type: 'open' | 'close') {
    sendActivityTrackerEvent(
      ActivityTrackerEventType.STICKY_APP_DOWNLOAD_INTERACTION_ACTION,
      {
        type,
        ctaId,
        page,
        urlPath,
        schemaVersion: '1.0',
      }
    );
  }

  function handleExpand() {
    hasInteracted.current = true;
    setIsExpanded(true);
    sendInteractionEvent('open');
  }

  function handleCollapse() {
    hasInteracted.current = true;
    setIsExpanded(false);
    sendInteractionEvent('close');
  }

  function handleDownloadClick() {
    sendActivityTrackerEvent(ActivityTrackerEventType.APP_DOWNLOAD_ACTION, {
      page,
      urlPath,
      ctaId,
      schemaVersion: '1.0',
    });
  }

  function handleKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Escape') {
      handleCollapse();
    }
  }

  const label = t('StickyAppCta.Label');

  return (
    <div
      className={styles.root}
      data-sticky-app-cta
      data-state={isExpanded ? 'expanded' : 'collapsed'}
      style={
        {
          '--sticky-cta-card-height': cardHeight
            ? `${cardHeight}px`
            : undefined,
        } as React.CSSProperties
      }
    >
      <button
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        onClick={handleExpand}
        aria-expanded={isExpanded}
        aria-controls={PANEL_ID}
        aria-label={label}
      >
        <img
          src={STICKY_CTA_APP_ICON}
          alt=""
          aria-hidden
          className={styles.appIcon}
        />
        <Heading as="span" variant="sm" className={styles.triggerLabel}>
          {label}
        </Heading>
      </button>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <div
        ref={panelRef}
        id={PANEL_ID}
        tabIndex={-1}
        role="group"
        aria-label={label}
        aria-hidden={!isExpanded}
        className={styles.panel}
        onKeyDown={handleKeyDown}
      >
        <div ref={cardRef} className={styles.desktopCard}>
          <div className={styles.cardHeader}>
            <img
              src={STICKY_CTA_APP_ICON}
              alt=""
              aria-hidden
              className={styles.appIcon}
            />
            <Heading as="span" variant="sm" className={styles.cardTitle}>
              {label}
            </Heading>
          </div>
          <Caption variant="lg-regular" className={styles.description}>
            {t('StickyAppCta.Description')}
          </Caption>
          <div className={styles.qrBox}>
            <BranchQRCode value={qrDeepLink} />
          </div>
          <ButtonMinimal
            className={styles.minimiseButton}
            onClick={handleCollapse}
            aria-label={t('StickyAppCta.Minimize')}
          >
            <ChevronDown size="md" border="regular" />
          </ButtonMinimal>
        </div>
        <div className={styles.mobileBanner}>
          <img
            src={STICKY_CTA_APP_ICON}
            alt=""
            aria-hidden
            className={styles.appIcon}
          />
          <div className={styles.mobileText}>
            <Caption variant="lg-bold" colour="base">
              {label}
            </Caption>
            <Caption variant="lg-regular">
              {t('StickyAppCta.Description')}
            </Caption>
          </div>
          <ExtendedLink
            href={downloadDeepLink}
            onClick={handleDownloadClick}
            className={styles.downloadCta}
          >
            <Body as="span" variant="sm-bold">
              {t('StickyAppCta.Download')}
            </Body>
          </ExtendedLink>
          <button
            type="button"
            className={styles.dismissButton}
            onClick={handleCollapse}
          >
            <Close size="sm" border="regular" />
            <VisuallyHidden>{t('StickyAppCta.Dismiss')}</VisuallyHidden>
          </button>
        </div>
      </div>
    </div>
  );
}
